import { DIFF_TAGS } from './constants';
import { WordDiff, DiffItem, Hunk, HunkLine } from './types';

/**
 * Calculate word-level differences using LCS (Longest Common Subsequence) algorithm
 * LCS is better than simple positional comparison because it can detect moved words
 * and provides more accurate matching, especially for reordered content
 */
export const getWordDiffsLCS = (
  oldText: string = '',
  newText: string = '',
): { leftWords: WordDiff[]; rightWords: WordDiff[] } => {
  // Handle edge cases: empty strings
  if (!oldText && !newText) {
    return { leftWords: [], rightWords: [] };
  }

  // Only new text exists (insertion)
  if (!oldText) {
    return {
      leftWords: [],
      rightWords: [{ type: 'insert', text: newText }],
    };
  }

  // Only old text exists (deletion)
  if (!newText) {
    return {
      leftWords: [{ type: 'delete', text: oldText }],
      rightWords: [],
    };
  }

  // Split into words while preserving whitespace as separate elements
  // This keeps spaces visible in the diff output
  const oldWords = oldText.split(/(\s+)/).filter(w => w !== '');
  const newWords = newText.split(/(\s+)/).filter(w => w !== '');

  // Create matrix for LCS calculation
  // We add +1 to dimensions for the empty prefix case
  const lcsMatrix: number[][] = Array(oldWords.length + 1)
    .fill(null)
    .map(() => Array(newWords.length + 1).fill(0));

  // Fill the LCS matrix
  // Each cell [i][j] represents the LCS length for prefixes oldWords[0..i-1] and newWords[0..j-1]
  for (let i = 1; i <= oldWords.length; i++) {
    for (let j = 1; j <= newWords.length; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        // Words match - extend the common subsequence
        lcsMatrix[i][j] = lcsMatrix[i - 1][j - 1] + 1;
      } else {
        // Take the maximum LCS length from either skipping old word or new word
        lcsMatrix[i][j] = Math.max(lcsMatrix[i - 1][j], lcsMatrix[i][j - 1]);
      }
    }
  }

  // Backtrack through the matrix to reconstruct the diff
  let i = oldWords.length;
  let j = newWords.length;
  const leftResult: WordDiff[] = [];
  const rightResult: WordDiff[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      // Words are equal - add to both sides
      leftResult.unshift({ type: 'equal', text: oldWords[i - 1] });
      rightResult.unshift({ type: 'equal', text: newWords[j - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcsMatrix[i][j - 1] >= lcsMatrix[i - 1][j])) {
      // New word doesn't exist in old text (insertion)
      rightResult.unshift({ type: 'insert', text: newWords[j - 1] });
      j--;
    } else if (i > 0 && (j === 0 || lcsMatrix[i][j - 1] < lcsMatrix[i - 1][j])) {
      // Old word doesn't exist in new text (deletion)
      leftResult.unshift({ type: 'delete', text: oldWords[i - 1] });
      i--;
    }
  }

  return { leftWords: leftResult, rightWords: rightResult };
};

export const buildHunks = (diff: DiffItem[], context: number): Hunk[] => {
  // Collect indexes of all changes
  const changeIndices = diff.reduce<number[]>((acc, item, index) => {
    if (item.tag !== DIFF_TAGS.EQUAL) acc.push(index);
    return acc;
  }, []);

  // Grouping close changes (within 2*context lines)
  const groupedChanges = changeIndices.reduce<number[][]>((groups, index) => {
    const lastGroup = groups[groups.length - 1];
    const lastIndex = lastGroup?.[lastGroup.length - 1] ?? -Infinity;

    if (index - lastIndex <= context * 2) {
      if (lastGroup) lastGroup.push(index);
      else groups.push([index]);
    } else {
      groups.push([index]);
    }

    return groups;
  }, []);

  // Create hunks for each group
  return groupedChanges.map(group => {
    const firstChange = Math.max(0, Math.min(...group) - context);
    const lastChange = Math.min(diff.length - 1, Math.max(...group) + context);
    const hunkSlice = diff.slice(firstChange, lastChange + 1);

    const lines = hunkSlice.flatMap((item: DiffItem): HunkLine[] => {
      if (item.tag === DIFF_TAGS.EQUAL) {
        return [
          {
            tag: DIFF_TAGS.EQUAL,
            oldLine: item.oldLine,
            newLine: item.newLine,
          },
        ];
      }
      if (item.tag === DIFF_TAGS.DELETE) {
        return [
          {
            tag: DIFF_TAGS.DELETE,
            oldLine: item.oldLine,
            newLine: '',
          },
        ];
      }
      if (item.tag === DIFF_TAGS.INSERT) {
        return [
          {
            tag: DIFF_TAGS.INSERT,
            oldLine: '',
            newLine: item.newLine,
          },
        ];
      }
      if (item.tag === DIFF_TAGS.CHANGE) {
        return [
          {
            tag: DIFF_TAGS.CHANGE,
            oldLine: item.oldLine,
            newLine: item.newLine,
          },
        ];
      }
      return [];
    });

    return { lines };
  });
};
